import React, { useState, useEffect } from 'react';
import { Settings, RefreshCw, X, Upload, Play, Info, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import TweetCard from './ui/TweetCard';
import RoamQuoteCard from './ui/RoamQuoteCard';
import ManualQuoteCard from './ui/ManualQuoteCard';
import InkQuoteApiConfig from './ui/InkQuoteApiConfig';
import ViewModeToggle from './ui/ViewModeToggle';
import FilteringTips from './ui/FilteringTips';
import SelectionTrace from './ui/SelectionTrace';
import SelectionExplainer from './ui/SelectionExplainer';
import RoamIcon from './ui/RoamIcon';
import { InkQuoteApi } from '../services/inkquoteApi';
import { adaptInkQuote } from '../utils/inkquoteAdapters';
import { useSkipCategories } from '../context/SkipCategoriesContext';
import { isRoamSource } from './utils';
import { useStagedEdit } from '../context/StagedEditContext';

// Twitter Bird Icon component
const TwitterIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M23.954 4.569c-.885.392-1.83.656-2.825.775 1.014-.608 1.794-1.569 2.163-2.724-.951.564-2.005.974-3.127 1.195-.897-.959-2.178-1.559-3.594-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.576-.666 2.476 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.23-.616v.062c0 2.385 1.693 4.374 3.946 4.827-.413.112-.849.171-1.296.171-.317 0-.626-.03-.927-.086.631 1.953 2.445 3.376 4.604 3.416-1.68 1.319-3.809 2.107-6.115 2.107-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.212 7.548 2.212 9.051 0 14-7.496 14-13.986 0-.21 0-.423-.015-.634.961-.695 1.8-1.562 2.46-2.549z" />
  </svg>
);

const InkQuoteManager = () => {
  const [quotes, setQuotes] = useState([]);
  // The constants the walk runs on, sent alongside the events so the explainer
  // cannot drift from the tuning that actually produced them.
  const [selectionConfig, setSelectionConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('inkquote_view_mode') || 'manage';
  });
  const { initializeFromTweets } = useSkipCategories();
  const { stagedEditItems, removeStagedEditItem, clearStagedEditItems } = useStagedEdit();

  // Integrations state
  const [integrations, setIntegrations] = useState([]);
  const [runningIntegrations, setRunningIntegrations] = useState(new Set());
  const [uploadingFile, setUploadingFile] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('inkquote_view_mode', viewMode);
  }, [viewMode]);

  const loadQuotes = async () => {
    if (!InkQuoteApi.isConfigured()) {
      setIsConfigOpen(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await InkQuoteApi.fetchRecentQuotes(100);
      // API returns {events: [...], total: N, limit: N}
      const events = data.events || [];
      setQuotes(events);
      setSelectionConfig(data.selection_config || null);

      // Initialize skip categories from adapted quotes
      const adaptedQuotes = events.map(quote => adaptInkQuote(quote).data);
      initializeFromTweets(adaptedQuotes);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load InkQuote data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadQuotes();
  }, []);

  const handleUpdateQuote = async (quoteId, updates) => {
    try {
      await InkQuoteApi.updateQuote(quoteId, updates);
      await loadQuotes(); // Reload to get updated data
    } catch (error) {
      console.error('Failed to update quote:', error);
      throw error;
    }
  };

  const handleConfigSave = () => {
    setIsConfigOpen(false);
    loadQuotes();
  };

  // Load integrations
  const loadIntegrations = async () => {
    if (!InkQuoteApi.isConfigured()) return;

    try {
      const response = await InkQuoteApi.getIntegrations();

      if (response && typeof response === 'object') {
        if (response.status && typeof response.status === 'object') {
          const integrationsList = Object.values(response.status);
          setIntegrations(integrationsList);
        } else if (response.integrations && Array.isArray(response.integrations)) {
          const integrationsList = response.integrations.map((name) => ({
            name,
            status: 'unknown',
            config_loaded: false,
            required_config: [],
            available: true
          }));
          setIntegrations(integrationsList);
        } else if (Array.isArray(response)) {
          setIntegrations(response);
        }
      }
    } catch (err) {
      console.error('Failed to load integrations:', err);
    }
  };

  // Run an integration
  const runIntegration = async (name) => {
    setRunningIntegrations(prev => new Set([...prev, name]));
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await InkQuoteApi.runIntegration(name);
      setSuccessMessage(
        `${name} integration completed: ${result.new || 0} new, ${result.updated || 0} updated, ${result.skipped || 0} skipped`
      );
      await Promise.all([loadIntegrations(), loadQuotes()]);
    } catch (err) {
      setError(`Failed to run ${name} integration: ${err.message}`);
    } finally {
      setRunningIntegrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(name);
        return newSet;
      });
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.json') && !fileName.endsWith('.js')) {
      setError('Please select a JSON (.json) or JavaScript (.js) file');
      return;
    }

    setUploadingFile(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await InkQuoteApi.uploadTwitterFile(file);
      setSuccessMessage(
        `Twitter import completed: ${result.new || 0} new tweets imported, ${result.skipped || 0} skipped`
      );
      await Promise.all([loadIntegrations(), loadQuotes()]);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  // Helper functions for integrations UI
  const getIntegrationIcon = (name, size = 24) => {
    const iconName = name?.toLowerCase() || '';
    if (iconName.includes('twitter')) {
      return <TwitterIcon size={size} />;
    }
    if (iconName.includes('roam')) {
      return <RoamIcon size={size} />;
    }
    return <span style={{ fontSize: size }}>🔌</span>;
  };

  const getStatusConfig = (integration) => {
    if (!integration.available) {
      return { color: 'text-gray-500', icon: XCircle, text: 'Not Available' };
    }
    if (integration.status === 'ready') {
      return { color: 'text-green-600 dark:text-green-400', icon: CheckCircle, text: 'Ready' };
    }
    if (integration.config_loaded) {
      return { color: 'text-amber-600 dark:text-amber-400', icon: AlertCircle, text: 'Needs Config' };
    }
    return { color: 'text-red-600 dark:text-red-400', icon: XCircle, text: 'Missing Config' };
  };

  // Load integrations when component mounts
  useEffect(() => {
    if (InkQuoteApi.isConfigured()) {
      loadIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize skip categories for staged items when they arrive
  useEffect(() => {
    if (stagedEditItems.length > 0) {
      initializeFromTweets(stagedEditItems);
    }
  }, [stagedEditItems, initializeFromTweets]);

  // Render a staged item card (items from search have different structure)
  const renderStagedCard = (item) => {
    // Determine the source type
    const source = item.source || 'like';

    // Ensure _inkquote_id is set for API calls
    // Items from search results may have the InkQuote UUID in different places:
    // - _inkquote_id (already set by adapter)
    // - _raw_metadata.original_id (for tweets - InkQuote UUID from download-exports.js)
    // - id (for roam quotes - already the InkQuote UUID)
    // Note: metadata.original_id is the Roam block UID, NOT the InkQuote UUID
    const inkquoteId = item._inkquote_id
      || item._raw_metadata?.original_id
      || item.id;

    const itemWithInkquoteId = {
      ...item,
      _inkquote_id: inkquoteId
    };

    if (isRoamSource(source)) {
      return (
        <RoamQuoteCard
          key={item.id}
          quote={itemWithInkquoteId}
          onUpdateQuote={handleUpdateQuote}
          viewMode={viewMode}
        />
      );
    }

    // Default to TweetCard for likes, bookmarks, and twitter sources
    return (
      <TweetCard
        key={item.id}
        tweet={itemWithInkquoteId}
        onUpdateTweet={(updatedTweet) =>
          handleUpdateQuote(updatedTweet._inkquote_id || updatedTweet.id, {
            content: updatedTweet.text,
            author: updatedTweet.user?.name
          })
        }
        onEditStateChange={() => {}}
        viewMode={viewMode}
      />
    );
  };

  const renderQuoteCard = (quote, index) => {
    const adapted = adaptInkQuote(quote);

    let card = null;
    switch (adapted.type) {
      case 'tweet':
        card = (
          <TweetCard
            tweet={adapted.data}
            onUpdateTweet={(updatedTweet) =>
              handleUpdateQuote(updatedTweet._inkquote_id, {
                content: updatedTweet.text,
                author: updatedTweet.user.name
              })
            }
            onEditStateChange={() => {}}
            viewMode={viewMode}
          />
        );
        break;
      case 'roam':
        card = (
          <RoamQuoteCard
            quote={adapted.data}
            onUpdateQuote={handleUpdateQuote}
            viewMode={viewMode}
          />
        );
        break;
      case 'manual':
        card = (
          <ManualQuoteCard
            quote={adapted.data}
            onUpdateQuote={handleUpdateQuote}
            viewMode={viewMode}
          />
        );
        break;
      default:
        return null;
    }

    // The same quote can appear twice in a display list, so the id alone is not
    // a key here the way it is everywhere else — this is a list of showings.
    return (
      <div key={`${quote.quote_id || quote.id}-${quote.displayed_at || index}`}>
        <SelectionTrace selection={quote.selection} />
        {card}
      </div>
    );
  };

  return (
    <div className="inkquote-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
        <div className="flex gap-2 items-center flex-wrap">
          <ViewModeToggle mode={viewMode} onModeChange={setViewMode} />
          <button
            onClick={loadQuotes}
            disabled={isLoading}
            className="shuffle-button disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            title="Refresh quotes"
          >
            <RefreshCw size={20} className={`inline-block sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => setIsConfigOpen(true)}
            className="shuffle-button flex items-center justify-center"
            title="Open settings"
          >
            <Settings size={20} className="inline-block sm:mr-2" />
            <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 p-4 rounded-lg mb-4 flex items-start gap-3">
          <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
          <p className="text-sm">{successMessage}</p>
        </div>
      )}

      {/* Show Integrations View */}
      {viewMode === 'integrations' ? (
        <div className="space-y-4">
          {/* Twitter File Upload */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-4">
              <TwitterIcon size={24} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload Twitter Data</h3>
            </div>
            <label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                disabled={uploadingFile}
                className="hidden"
                id="twitter-file-upload"
              />
              <label
                htmlFor="twitter-file-upload"
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium
                  transition-colors cursor-pointer text-white
                  ${uploadingFile
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                  }
                `}
              >
                {uploadingFile ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    <span>Select Twitter File</span>
                  </>
                )}
              </label>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Twitter exports are .json files exported directly from a user's likes page.
            </p>
          </div>

          {/* Available Integrations */}
          {integrations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Available Integrations</h3>
                </div>
                <button
                  onClick={loadIntegrations}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Refresh integrations"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
              <div className="space-y-3">
                {integrations.map((integration) => {
                  const isRunning = runningIntegrations.has(integration.name);
                  const canRun = integration.available && integration.status === 'ready' && !isRunning;
                  const statusConfig = getStatusConfig(integration);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div
                      key={integration.name}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {getIntegrationIcon(integration.name, 24)}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                            {integration.name}
                          </h4>
                          <div className={`flex items-center gap-1 text-sm ${statusConfig.color}`}>
                            <StatusIcon size={14} />
                            <span>{statusConfig.text}</span>
                          </div>
                          {integration.required_config &&
                           integration.required_config.length > 0 &&
                           integration.status !== 'ready' && (
                            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                              <p className="font-semibold mb-1">Required Configuration:</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                {integration.required_config.map((config, idx) => (
                                  <li key={idx}>{config}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => runIntegration(integration.name)}
                        disabled={!canRun}
                        className={`
                          flex items-center gap-2 px-4 py-2 rounded-lg font-medium
                          transition-colors whitespace-nowrap
                          ${canRun
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                          }
                        `}
                      >
                        {isRunning ? (
                          <>
                            <RefreshCw size={16} className="animate-spin" />
                            <span>Running...</span>
                          </>
                        ) : (
                          <>
                            <Play size={16} />
                            <span>Run</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      ) : (
        <>
          {/* Filtering Tips */}
          <FilteringTips />

          {/* How the panel chooses */}
          <SelectionExplainer config={selectionConfig} />

          {/* Staged for Edit Section */}
      {stagedEditItems.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Staged for Edit ({stagedEditItems.length})
            </h3>
            <button
              onClick={clearStagedEditItems}
              className="text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 flex items-center gap-1"
            >
              <X size={14} />
              Clear all manual edits
            </button>
          </div>
          <div className="space-y-4 pb-4 mb-4 border-b-2 border-dashed border-purple-300 dark:border-purple-700">
            {stagedEditItems.map((item) => (
              <div key={item.id} className="relative">
                <button
                  onClick={() => removeStagedEditItem(item.id)}
                  className="absolute top-2 right-2 z-10 bg-gray-500 hover:bg-red-500 text-white rounded-full p-1 shadow-md transition-colors"
                  title="Remove from staged"
                >
                  <X size={14} />
                </button>
                {renderStagedCard(item)}
              </div>
            ))}
          </div>
        </div>
      )}

          {/* Error State */}
          {error && (
            <div className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 p-4 rounded mb-4">
              <p className="font-semibold">Error loading quotes:</p>
              <p>{error}</p>
              <button
                onClick={() => setIsConfigOpen(true)}
                className="mt-2 text-sm underline"
              >
                Check API configuration
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8 dark:text-gray-300">
              Loading quotes...
            </div>
          )}

          {/* Quotes List */}
          {!isLoading && !error && (
            <div className="space-y-4">
              {quotes.length === 0 ? (
                <div className="text-center py-8 dark:text-gray-300">
                  No quotes found. Check your API configuration.
                </div>
              ) : (
                quotes.map(renderQuoteCard)
              )}
            </div>
          )}
        </>
      )}

      {/* Configuration Modal */}
      <InkQuoteApiConfig
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        onSave={handleConfigSave}
      />
    </div>
  );
};

export default InkQuoteManager;
